// Adaptive Calibration for FTT Experiment
// Adjusts scenario difficulty based on participant performance

import { EmailData } from '../components/phases/EmailTask';
import { FTTClassification } from './fttClassifier';

interface CalibrationState {
  participantId: string;
  detectionAccuracy: number;
  gistVerbatimBalance: number;
  adjustmentHistory: Array<{
    timestamp: number;
    adjustment: number;
    reason: string;
  }>;
  performanceMetrics: {
    totalResponses: number;
    correctDetections: number;
    falsePositives: number;
    falseNegatives: number;
    avgResponseTime: number;
    gistResponseCount: number;
    verbatimResponseCount: number;
  };
}

export class AdaptiveCalibrator {
  private calibrationState: Map<string, CalibrationState> = new Map();
  private adjustmentThresholds = {
    highAccuracy: 0.8,      // Above this, increase difficulty
    lowAccuracy: 0.4,       // Below this, decrease difficulty
    gistDominance: 0.7,     // Too much gist processing
    verbatimDominance: 0.3, // Too much verbatim processing
  };
  
  // Analyze participant performance
  analyzePerformance(
    participantId: string, 
    recentResponses: Array<{
      isPhishing: boolean;
      clicked: boolean;
      fttClassification?: FTTClassification;
      responseTime: number;
    }>
  ): void {
    if (recentResponses.length === 0) return;
    
    // Get last 10 responses for recent performance
    const last10 = recentResponses.slice(-10);
    
    // Calculate detection accuracy
    const correctDetections = last10.filter(r => 
      (r.isPhishing && !r.clicked) || (!r.isPhishing && r.clicked)
    ).length;
    const accuracy = correctDetections / last10.length;
    
    // Calculate processing mode balance
    const gistCount = last10.filter(r => r.fttClassification?.mode === 'gist').length;
    const verbatimCount = last10.filter(r => r.fttClassification?.mode === 'verbatim').length;
    const balance = last10.length > 0 ? gistCount / last10.length : 0.5;
    
    // Calculate false positives/negatives
    const falsePositives = last10.filter(r => !r.isPhishing && !r.clicked).length;
    const falseNegatives = last10.filter(r => r.isPhishing && r.clicked).length;
    
    // Average response time
    const avgResponseTime = last10.reduce((sum, r) => sum + r.responseTime, 0) / last10.length;
    
    // Update or create state
    const state = this.calibrationState.get(participantId) || {
      participantId,
      detectionAccuracy: 0.5,
      gistVerbatimBalance: 0.5,
      adjustmentHistory: [],
      performanceMetrics: {
        totalResponses: 0,
        correctDetections: 0,
        falsePositives: 0,
        falseNegatives: 0,
        avgResponseTime: 0,
        gistResponseCount: 0,
        verbatimResponseCount: 0
      }
    };
    
    state.detectionAccuracy = accuracy;
    state.gistVerbatimBalance = balance;
    state.performanceMetrics = {
      totalResponses: recentResponses.length,
      correctDetections: correctDetections,
      falsePositives,
      falseNegatives,
      avgResponseTime,
      gistResponseCount: gistCount,
      verbatimResponseCount: verbatimCount
    };
    
    this.calibrationState.set(participantId, state);
  }
  
  // Get adjusted scenario based on performance
  getAdjustedScenario(baseScenario: EmailData, participantId: string): EmailData {
    const state = this.calibrationState.get(participantId);
    if (!state) return baseScenario;
    
    let adjustedScenario = { ...baseScenario };
    let adjustmentReason = '';
    let adjustmentMagnitude = 0;
    
    // High accuracy - make it harder
    if (state.detectionAccuracy > this.adjustmentThresholds.highAccuracy) {
      adjustedScenario = this.increaseSubtlety(adjustedScenario);
      adjustmentReason = 'High accuracy - increased subtlety';
      adjustmentMagnitude = 0.1;
      
      // For very high performers, add more conflict
      if (state.detectionAccuracy > 0.9) {
        adjustedScenario = this.increaseGistVerbatimConflict(adjustedScenario);
        adjustmentReason += ' + increased conflict';
        adjustmentMagnitude = 0.2;
      }
    }
    
    // Low accuracy - make it easier
    else if (state.detectionAccuracy < this.adjustmentThresholds.lowAccuracy) {
      adjustedScenario = this.decreaseSubtlety(adjustedScenario);
      adjustmentReason = 'Low accuracy - decreased subtlety';
      adjustmentMagnitude = -0.1;
    }
    
    // Too much gist processing - add misleading gist cues
    if (state.gistVerbatimBalance > this.adjustmentThresholds.gistDominance) {
      adjustedScenario = this.misleadGistProcessing(adjustedScenario);
      adjustmentReason = 'Gist-heavy - added misleading gist cues';
      adjustmentMagnitude = 0.15;
    }
    
    // Too much verbatim processing - add time pressure
    else if (state.gistVerbatimBalance < this.adjustmentThresholds.verbatimDominance) {
      adjustedScenario.timeLimit = 5000; // 5 second limit
      adjustedScenario.timePressure = true;
      adjustmentReason = 'Verbatim-heavy - added time pressure';
      adjustmentMagnitude = -0.1;
    }
    
    // Log adjustment
    if (adjustmentReason) {
      this.logAdjustment(participantId, adjustmentMagnitude, adjustmentReason);
    }
    
    return adjustedScenario;
  }
  
  // Increase subtlety of phishing indicators
  private increaseSubtlety(scenario: EmailData): EmailData {
    if (!scenario.isPhishing) return scenario;
    
    return {
      ...scenario,
      // Make domain differences more subtle
      sender: scenario.sender.replace(/companyy\.com/g, 'company.corn')
        .replace(/microsoft-notify/g, 'microsoft-notification'),
      
      // Make verbatim cues less visible
      fullContent: scenario.fullContent?.replace(
        /class="verbatim-cue"/g, 
        'class="verbatim-cue very-subtle"'
      ),
      
      // Reduce visual warnings
      securityWarnings: scenario.securityWarnings?.slice(0, 1),
      
      // Increase complexity
      complexity: Math.min((scenario.complexity || 0.5) + 0.2, 1),
      conflictLevel: 'high'
    };
  }
  
  // Decrease subtlety - make threats more obvious
  private decreaseSubtlety(scenario: EmailData): EmailData {
    if (!scenario.isPhishing) return scenario;
    
    return {
      ...scenario,
      // Make domain differences more obvious
      sender: scenario.sender.replace(/@[^>]+/, '@SUSPICIOUS-DOMAIN.fake'),
      
      // Make verbatim cues more visible
      fullContent: scenario.fullContent?.replace(
        /class="verbatim-cue subtle"/g,
        'class="verbatim-cue obvious"'
      ) + '<p class="warning-highlight">⚠️ Warning: External sender</p>',
      
      // Add more warnings
      securityWarnings: [
        ...(scenario.securityWarnings || []),
        'Suspicious sender domain',
        'External link detected'
      ],
      
      // Decrease complexity
      complexity: Math.max((scenario.complexity || 0.5) - 0.2, 0),
      conflictLevel: 'low'
    };
  }
  
  // Add misleading gist cues
  private misleadGistProcessing(scenario: EmailData): EmailData {
    if (scenario.isPhishing) {
      // Add legitimate-looking gist cues to phishing
      return {
        ...scenario,
        gistCues: [
          ...(scenario.gistCues || []),
          'Official company format',
          'Professional tone',
          'Expected communication'
        ],
        fullContent: `
          <div class="legitimate-looking-header">
            <img src="/fake-company-logo.png" alt="Company Logo" class="official-logo" />
            <p class="tagline">Your Trusted IT Partner</p>
          </div>
          ${scenario.fullContent}
        `,
        conflictLevel: 'high'
      };
    } else {
      // Add suspicious-looking gist cues to legitimate emails
      return {
        ...scenario,
        subject: '🚨 URGENT: ' + scenario.subject,
        gistCues: ['URGENT marker', ...(scenario.gistCues || [])],
        fullContent: scenario.fullContent?.replace(
          '<p>',
          '<p class="urgent-text">⚠️ IMMEDIATE ACTION REQUIRED: '
        ),
        conflictLevel: 'medium'
      };
    }
  }
  
  // Increase gist-verbatim conflict
  private increaseGistVerbatimConflict(scenario: EmailData): EmailData {
    return {
      ...scenario,
      // Strong gist cues opposing verbatim details
      gistCues: scenario.isPhishing 
        ? ['Urgent security update', 'IT Department', 'Protect your account']
        : ['Suspicious request', 'Unusual timing', 'Generic greeting'],
      
      verbatimCues: scenario.isPhishing
        ? ['domainMismatch', 'unverifiableUrgency', 'externalPaymentLink']
        : ['exactOrgDomain', 'contextualDetails', 'validContactInfo'],
      
      conflictLevel: 'high',
      complexity: 0.9
    };
  }
  
  // Log adjustment for analysis
  private logAdjustment(participantId: string, adjustment: number, reason: string): void {
    const state = this.calibrationState.get(participantId);
    if (state) {
      state.adjustmentHistory.push({
        timestamp: Date.now(),
        adjustment,
        reason
      });
      
      // Keep only last 50 adjustments
      if (state.adjustmentHistory.length > 50) {
        state.adjustmentHistory = state.adjustmentHistory.slice(-50);
      }
    }
  }
  
  // Get calibration report
  getCalibrationReport(participantId: string): CalibrationState | null {
    return this.calibrationState.get(participantId) || null;
  }
  
  // Export calibration data for analysis
  exportCalibrationData(): Array<CalibrationState> {
    return Array.from(this.calibrationState.values());
  }
}

// Singleton instance
export const calibrator = new AdaptiveCalibrator();